// src/components/settings/InstitutionSettings.jsx
import React, { useState, useEffect } from 'react';
import { 
  Building, 
  School, 
  Users, 
  MapPin, 
  Plus, 
  Search, 
  Edit, 
  Trash2,
  UserPlus,
  UserMinus,
  Eye,
  Filter,
  AlertCircle
} from 'lucide-react';
import { InstitutionService } from '../../services/institutionService';
import { useAuth } from '../../contexts/AuthContext';

const InstitutionSettings = ({ onUnsavedChanges }) => {
  const { currentUser } = useAuth();
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    loadInstitutions();
  }, []);

  const loadInstitutions = async () => {
    try {
      setLoading(true);
      const userInstitutions = await InstitutionService.getUserInstitutions();
      setInstitutions(userInstitutions);
    } catch (err) {
      setError('Failed to load institutions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredInstitutions = institutions.filter(institution => {
    const matchesSearch = institution.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         institution.schoolName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || institution.type === filterType;
    return matchesSearch && matchesFilter;
  });

  const getInstitutionIcon = (type) => {
    switch (type) {
      case 'School': return <School className="w-5 h-5 text-blue-500" />;
      default: return <Building className="w-5 h-5 text-gray-500" />;
    }
  };

  const getInstitutionBadge = (type) => {
    const colors = {
      'School': 'bg-blue-100 text-blue-800',
      'District': 'bg-green-100 text-green-800',
      'Province': 'bg-purple-100 text-purple-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[type] || 'bg-gray-100 text-gray-800'}`}>
        {type}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Institution Management</h3>
          <p className="text-sm text-gray-600">Manage your educational institutions</p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search institutions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>
        
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm appearance-none"
          >
            <option value="all">All Types</option>
            <option value="School">Schools</option>
            <option value="District">Districts</option>
            <option value="Province">Provinces</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {/* Institutions Grid */}
      {filteredInstitutions.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredInstitutions.map((institution) => (
            <div
              key={institution.id}
              className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow duration-200"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  {getInstitutionIcon(institution.type)}
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">
                      {institution.schoolName || institution.name}
                    </h4>
                    {getInstitutionBadge(institution.type)}
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="space-y-2 mb-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span>{institution.district}, {institution.province}</span>
                </div>
                {institution.schoolPhase && (
                  <div className="text-sm text-gray-600">
                    Phase: {institution.schoolPhase}
                  </div>
                )}
              </div>

              {/* Members */}
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                <Users className="w-4 h-4" />
                <span>{institution.members?.length || 0} members</span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-white rounded-md border border-gray-200 transition-colors">
                  <Eye className="w-3 h-3" />
                  View
                </button>
                {institution.createdBy === currentUser?.uid && (
                  <>
                    <button className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-50 rounded-md border border-blue-200 transition-colors">
                      <Edit className="w-3 h-3" />
                      Edit
                    </button>
                    <button className="flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium text-green-700 hover:bg-green-50 rounded-md border border-green-200 transition-colors">
                      <UserPlus className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">No institutions found</h4>
          <p className="text-gray-600">
            {searchTerm || filterType !== 'all' 
              ? 'Try adjusting your search or filters' 
              : "You haven't joined any institutions yet"}
          </p>
        </div>
      )}
    </div>
  );
};

export default InstitutionSettings;